/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Controller, useForm, UseFormReturn } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import {
  ArrowLongRightIcon,
  BoltIcon,
  BuildingOfficeIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import { FormPage } from '../../tailwindComponents/FormPage';
import { Input } from '../../tailwindComponents/Input';
import { Select } from '../../tailwindComponents/Select';
import { Datepicker } from '../../tailwindComponents/Datepicker';
import { Timepicker } from '../../tailwindComponents/Timepicker';
import { Textarea } from '../../tailwindComponents/Textarea';
import { Subheader } from '../../tailwindComponents/Subheader';
import { ToggleSwitch } from '../../tailwindComponents/ToggleSwitch';
import { ToggleButtonGroup } from '../../tailwindComponents/ToggleButtonGroup';
import { ShipmentMethodCard } from '../../tailwindComponents/ShipmentMethodCard';
import {
  TransportOrderFormValues,
  defaultFormValues,
  TransportQuote,
  TransportQuotesResponse,
} from './types';
import { getQuoteKey, isSameQuote } from './quoteIdentity';
import { buildSchemas, stepFields } from './schema';
import { deriveUnitType } from './quoteConstants';
import {
  ShipmentMethodBucket,
  bucketForSource,
  canCourierTakeLoad,
  canVehicleCarryLoad,
  describeShipmentMethodBucket,
  filterQuotesByServiceLevel,
  formatCourierDistance,
  formatPrice,
  formatSourceLabel,
  formatVehicleLabel,
  isQuoteSelectionRequiredForChosenBucket,
  isSelectedQuoteVisibleAfterFilterChange,
  isShipmentMethodSelectionRequired,
  nextShipmentMethod,
  shipmentMethodBucketsForLoad,
  withoutCourierQuotesWhenCourierCannotTakeLoad,
} from './shipmentMethods';
import { QuoteOptionCard } from './QuoteOptionCard';
import {
  isBackendResolvedPalletType,
  toLoadHeightCm,
  toPalletCount,
  toPerPalletWeightKg,
  toTotalWeightKg,
} from './palletDimensions';
import {
  fetchPostcodeAreaOptions,
  mergeSuggestionSections,
  MIN_POSTCODE_AREA_QUERY_LENGTH,
} from './postcodeAreaSearch';
import type {
  AddressSuggestionOption,
  TransportSuggestionOption,
} from './postcodeAreaSearch';
import { DespatchNoteSummaryPanel } from './DespatchNoteSummaryPanel';
import { ScheduleAndHandlingPanel } from './ScheduleAndHandlingPanel';
import { useAuthHttp } from '../../hooks';
import {
  TRANSPORT_ORDER_QUOTES,
  FLEET_CATEGORIES,
  PALLET_WEIGHT_UNITS,
  DIMENSION_UNITS,
  HERE_APIKEY,
} from '../../constants';
import {
  STANDARD_PALLET_TYPE_VALUES,
  describeTierExcess,
  getPalletTypeLabel,
} from '../../constants/pallets';
import { SERVICE_LEVELS } from '../../constants/ratecards';
import { Autocomplete } from '../../tailwindComponents/Autocomplete';
import { nowLocalDate } from '@utils';

const STEPS = ['Load details', 'Collection → Delivery', 'Confirmation'];

const carrierOptions: { label: string; value: string }[] = [];

const palletTypeOptions = [
  ...STANDARD_PALLET_TYPE_VALUES.map((value) => ({
    label: getPalletTypeLabel(value),
    value,
  })),
  { label: 'Mixed GB Pallet', value: 'Mixed GB Pallet' },
  { label: 'Mixed EU Pallet', value: 'Mixed EU Pallet' },
  { label: 'Custom Pallet', value: 'Custom Pallet' },
  { label: 'Unpalletised', value: 'Unpalletised' },
  { label: 'Other', value: 'Other' },
];

const serviceLevelOptions = [
  { label: 'Any', value: '' },
  ...SERVICE_LEVELS.map((level) => ({ label: level, value: level })),
];

const packagingOptions = [
  { label: 'Palletised', value: 'Palletised' },
  { label: 'Shrink-wrapped', value: 'Shrink-wrapped' },
  { label: 'Boxed', value: 'Boxed' },
  { label: 'Loose', value: 'Loose' },
  { label: 'Strapped', value: 'Strapped' },
];

// "Any" is the semantic of "no preference" — its empty-string value maps to the
// form's default (unset). Placed first so users can clear a prior selection without
// having to wipe the field manually.
const vehicleOptions = [
  { label: 'Any', value: '' },
  ...FLEET_CATEGORIES.map((c) => ({ label: c.label, value: c.value })),
];

const curtainBoxOptions = [
  { label: 'Any', value: '' },
  { label: 'Curtain', value: 'curtain' },
  { label: 'Box body', value: 'box-body' },
  { label: 'Either', value: 'either' },
];

const yesNoOptions = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
];

const unitOptions = DIMENSION_UNITS.map((u) => ({
  label: u.label,
  value: u.value,
}));

const weightUnitOptions = PALLET_WEIGHT_UNITS.map((u) => ({
  label: u.label,
  value: u.value,
}));

// Values are ISO 4217 codes (what the backend validates against); the symbol is
// only the display label. Sending the symbol (e.g. "£") fails server validation.
const currencyOptions = [
  { label: '£', value: 'GBP' },
  { label: '$', value: 'USD' },
  { label: '€', value: 'EUR' },
];

const waitingTimeUnitOptions = [
  { label: 'minutes', value: 'minutes' },
  { label: 'hours', value: 'hours' },
];

/**
 * Small pill shown above a field that was pre-filled from the visitor's Holly
 * chat (transport quote → sign-up hand-off). Matches the form's other field
 * markers ("Auto-set from SKU", "Sourced from despatch note") so it stays on
 * theme. Render `<HollyFieldBadge show={...} />`; it returns null when `show`
 * is false so call sites stay terse.
 */
function HollyFieldBadge({ show }: { show?: boolean }) {
  if (!show) return null;
  return (
    <span
      className='mb-1 inline-flex w-fit items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-200'
      title='Pre-filled from your Holly chat — please review and adjust if needed.'
    >
      From your Holly chat
    </span>
  );
}

function SkeletonCard() {
  return (
    <div className='flex h-60 w-[220px] flex-col items-center rounded-xl border-2 border-gray-200 bg-white p-4'>
      <div className='mb-2 h-5 w-20 animate-pulse rounded-full bg-gray-200' />
      <div className='mb-1 h-4 w-24 animate-pulse rounded bg-gray-100' />
      <div className='mb-2 h-3 w-16 animate-pulse rounded bg-gray-100' />
      <div className='mb-1 h-7 w-16 animate-pulse rounded bg-gray-200' />
      <div className='mb-3 h-3 w-20 animate-pulse rounded bg-gray-100' />
      <div className='mt-auto h-9 w-full animate-pulse rounded-lg bg-gray-200' />
    </div>
  );
}

// Render a date in the browser's own locale (e.g. "6 June 2026" in en-GB).
// dayjs parses "YYYY-MM-DD" as local time, so `.toDate()` avoids the UTC
// off-by-one you'd get from `new Date('2026-06-06')` in negative-offset zones.
function formatLocaleDate(value?: string | Date | null): string {
  if (!value) return '\u2014';
  const d = dayjs(value);
  if (!d.isValid()) return '\u2014';
  return d.toDate().toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

// Bias suggestions to the UK (Flox is UK-focused per CLAUDE.md). London coordinates
// give sensible nearby-first ordering when the user starts typing a short postcode.
// countrySet=GBR restricts results to the UK; switch to a form-driven country code
// when cross-border support lands (US-015).
function fetchAddressSuggestions(
  input: string
): Promise<AddressSuggestionOption[]> {
  return fetch(
    `https://autosuggest.search.hereapi.com/v1/autosuggest?apiKey=${HERE_APIKEY}&q=${encodeURIComponent(
      input
    )}&at=51.5074,-0.1278&in=countryCode:GBR&limit=7`
  )
    .then((response) => response.json())
    .then((data) =>
      // HARD filter: only show suggestions with a postcode. Without it we can't
      // resolve to a rate-card postcode zone or match against warehouse cutoff
      // lookups, so offering these results is a trap — the user picks them, we
      // then have nothing to feed the quote engine.
      (data.items ?? [])
        .filter((item: any) => item.address)
        .map((item: any) => ({
          id: item.id,
          label: item.address.label,
          city: item.address.city,
          country: item.address.countryName,
        }))
    )
    .catch((error) => {
      console.error('Error fetching suggestions:', error);
      return [];
    });
}

const SUGGESTION_DEBOUNCE_MS = 300;

type SuggestionTarget =
  | 'pickupPostcode'
  | 'dropoffPostcode'
  | 'pickupAddress'
  | 'dropoffAddress';

const emptySuggestionState: Record<
  SuggestionTarget,
  TransportSuggestionOption[]
> = {
  pickupPostcode: [],
  dropoffPostcode: [],
  pickupAddress: [],
  dropoffAddress: [],
};

type SuggestionSearchState = 'idle' | 'searching' | 'empty' | 'results';

const idleSearchState: Record<SuggestionTarget, SuggestionSearchState> = {
  pickupPostcode: 'idle',
  dropoffPostcode: 'idle',
  pickupAddress: 'idle',
  dropoffAddress: 'idle',
};

interface TransportOrderFormProps {
  onSubmit: (data: TransportOrderFormValues) => void;
  isSubmitting: boolean;
  header: string;
  pages: { name: string; href: string; current: boolean }[];
  initialValues?: Partial<TransportOrderFormValues>;
  isFromDespatchNote?: boolean;
  // When true, the form was pre-filled from a Holly chat session. Fields present
  // in `initialValues` get a "From your Holly chat" badge so the user can see
  // what was carried over vs. what they still need to complete.
  isFromHolly?: boolean;
  supplyChainProcessId?: string | null;
  despatchNoteNumber?: string | null;
  purchaseOrderNumber?: string | null;
  collectionLat?: number | null;
  collectionLon?: number | null;
  // Delivery geo from the despatch-note prep (customer record). Threaded so the quote
  // request still carries a destination signal when the customer has no postcode.
  deliveryLat?: number | null;
  deliveryLon?: number | null;
  // Per-pallet weight from the despatch-note prep. The `weight` form field is the TOTAL
  // consignment weight (feeds LSP weight bands + persistence); this feeds the network
  // per-pallet cap gate via fetchQuotes. SCP only — standalone leaves it undefined and the
  // backend derives per-pallet from total ÷ quantity.
  averagePalletWeight?: number | null;
  palletTypeWasDefaulted?: boolean;
  autoSetFlags?: {
    fragile?: boolean;
  };
  // Scheduling constraints (US-005) — "HH:mm" strings. SCP mode only; standalone mode
  // leaves these undefined so validation is a no-op.
  dispatchCutoffTime?: string | null;
  customerOperatingHoursFrom?: string | null;
  customerOperatingHoursTo?: string | null;
  skuLines?: {
    name: string;
    quantity: number;
    palletisingType?: string | null;
  }[];
}

export function TransportOrderForm({
  onSubmit,
  isSubmitting,
  header,
  pages,
  initialValues,
  isFromDespatchNote = false,
  isFromHolly = false,
  supplyChainProcessId = null,
  despatchNoteNumber,
  purchaseOrderNumber,
  collectionLat,
  collectionLon,
  deliveryLat,
  deliveryLon,
  averagePalletWeight,
  palletTypeWasDefaulted = false,
  autoSetFlags,
  dispatchCutoffTime,
  customerOperatingHoursFrom,
  customerOperatingHoursTo,
  skuLines,
}: TransportOrderFormProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const authHttp = useAuthHttp();
  const [quotes, setQuotes] = useState<TransportQuote[]>([]);
  const [isLoadingQuotes, setIsLoadingQuotes] = useState(false);
  const [quotesError, setQuotesError] = useState<string | null>(null);
  const latestQuoteRequestId = useRef(0);
  const [serviceLevelFilter, setServiceLevelFilter] = useState('');
  const liveFormInstanceRef = useRef<UseFormReturn<TransportOrderFormValues> | null>(
    null
  );

  const stepSchemas = useMemo(
    () =>
      buildSchemas(isFromDespatchNote, {
        dispatchCutoffTime,
        customerOperatingHoursFrom,
        customerOperatingHoursTo,
      }),
    [
      isFromDespatchNote,
      dispatchCutoffTime,
      customerOperatingHoursFrom,
      customerOperatingHoursTo,
    ]
  );

  const chosenShipmentMethod =
    liveFormInstanceRef.current?.getValues('shipmentMethod') ?? '';
  const isQuoteSelectionRequired = isQuoteSelectionRequiredForChosenBucket(
    quotes,
    chosenShipmentMethod,
    serviceLevelFilter,
    isLoadingQuotes,
    quotesError
  );
  const isShipmentMethodRequired = isShipmentMethodSelectionRequired(
    quotes,
    isLoadingQuotes,
    quotesError
  );

  const methods = useForm<TransportOrderFormValues>({
    shouldUnregister: false,
    resolver: yupResolver(stepSchemas[currentStep - 1]),
    context: { isQuoteSelectionRequired, isShipmentMethodRequired },
    mode: 'onChange',
    defaultValues: {
      ...defaultFormValues,
      collectionDate: nowLocalDate(),
      deliveryDate: nowLocalDate(),
      ...initialValues,
    },
  });
  liveFormInstanceRef.current = methods;

  const { formState, control, watch, setValue, getValues, clearErrors } =
    methods;

  // The set of fields Holly pre-filled — used to badge them. Derived from the
  // keys of `initialValues` (the Holly prefill object) only on the Holly path;
  // the despatch-note path has its own "Sourced from despatch note" markers.
  const hollyFields = useMemo(
    () =>
      isFromHolly && initialValues
        ? new Set(Object.keys(initialValues))
        : new Set<string>(),
    [isFromHolly, initialValues]
  );
  const isHollyField = (name: keyof TransportOrderFormValues) =>
    hollyFields.has(name as string);

  // Watch for controlled values
  const deadlineType = watch('deadlineType');
  const stackable = watch('stackable');
  const overhang = watch('overhang');

  // Load/route inputs that drive the debounced quote fetch behind the step 2
  // selection grid (fetched live from step 1 — no step gating).
  const collectionPostcode = watch('collectionPostcode');
  const deliveryPostcode = watch('deliveryPostcode');
  const totalPalletsValue = watch('totalPallets');
  const palletTypeValue = watch('palletType');
  const weightValue = watch('weight');
  const weightUnitValue = watch('weightUnit');
  const heightValue = watch('height');
  const heightUnitValue = watch('heightUnit');
  const palletLengthValue = watch('palletLength');
  const palletWidthValue = watch('palletWidth');
  const tailLiftValue = watch('tailLiftRequired');
  const moffettValue = watch('moffettRequired');
  const amDeliveryValue = watch('amDeliveryRequired');
  const pmDeliveryValue = watch('pmDeliveryRequired');
  const timedDeliveryValue = watch('timedDeliveryRequired');
  const saturdayAmDeliveryValue = watch('saturdayAmDeliveryRequired');
  const insuranceCoverValue = watch('insuranceCover');
  // Vehicle preferences — sent to the quote engine so pallet networks are excluded
  // when the shipper wants a specific vehicle or body type, and LSP rate cards are
  // narrowed to the matching VehicleType. "Any" maps to the empty string.
  const preferredVehicleValue = watch('preferredVehicle');
  const curtainSideOrBoxBodyValue = watch('curtainSideOrBoxBody');
  const shipmentMethodValue = watch('shipmentMethod');

  const isDedicatedVehicleRequired =
    !!preferredVehicleValue || !!curtainSideOrBoxBodyValue || !!moffettValue;

  const totalWeightKg = toTotalWeightKg(weightValue, weightUnitValue);
  const totalPallets = toPalletCount(totalPalletsValue);

  const loadSize = useMemo(
    () => ({ totalWeightKg, totalPallets }),
    [totalWeightKg, totalPallets]
  );

  const shipmentMethodBuckets = shipmentMethodBucketsForLoad(loadSize);

  const vehicleOptionsForTheLoad = vehicleOptions.filter((option) =>
    canVehicleCarryLoad(option.value, loadSize)
  );

  const palletTierExcessMessage = describeTierExcess(
    palletTypeValue,
    toLoadHeightCm(heightValue, heightUnitValue),
    toPerPalletWeightKg(
      averagePalletWeight,
      weightValue,
      totalPalletsValue,
      weightUnitValue
    )
  );

  // Standard pallet footprints are now filled server-side: the SKU and despatch-note read
  // models carry them (PrepareFromDespatchNote returns them), so the form no longer
  // reconstructs them from a client-side table. In standalone mode the user enters pallet
  // length/width directly when a non-standard type needs them.

  // Captured lat/lon from HERE lookups — used by fetchQuotes when standalone mode
  // doesn't get them from the despatch-note props. Falls back to props when not set.
  const [capturedCollectionLat, setCapturedCollectionLat] = useState<
    number | null
  >(null);
  const [capturedCollectionLon, setCapturedCollectionLon] = useState<
    number | null
  >(null);
  const [capturedDeliveryLat, setCapturedDeliveryLat] = useState<number | null>(
    null
  );
  const [capturedDeliveryLon, setCapturedDeliveryLon] = useState<number | null>(
    null
  );

  const fetchQuotes = useCallback(async () => {
    if (!collectionPostcode || !deliveryPostcode) return;
    const palletsNumberGuard = Number(totalPalletsValue);
    if (!Number.isFinite(palletsNumberGuard) || palletsNumberGuard <= 0) return;
    if (!palletTypeValue) return;
    const requestId = latestQuoteRequestId.current + 1;
    latestQuoteRequestId.current = requestId;
    const isLatestQuoteRequest = () =>
      requestId === latestQuoteRequestId.current;
    setIsLoadingQuotes(true);
    setQuotesError(null);
    try {
      const palletsNumber = Number(totalPalletsValue);
      const weightNumber = Number(weightValue);
      const heightNumber = Number(heightValue);
      const params: Record<string, string | number | boolean> = {
        fromPostcode: collectionPostcode,
        toPostcode: deliveryPostcode,
        // ISO 3166-1 alpha-3 — the ES NetworkPostcodeLookup stores this as "GBR"
        // and the cache uses an exact-match TermQuery, so "GB" silently returns
        // zero hits. Swap to form-driven value when US-015 (cross-border) lands.
        countryCode: 'GBR',
        unitType: deriveUnitType(palletTypeValue),
        // Forward the pallet type so the backend can fill the standard footprint for predefined
        // types when no dimensions are sent (standalone). We don't compute dimensions here.
        palletType: palletTypeValue,
        quantity:
          Number.isFinite(palletsNumber) && palletsNumber > 0
            ? palletsNumber
            : 1,
      };
      // Capability flags — backend uses these to (a) skip ambient-only NetworkRateCards
      // and (b) filter vehicle-linked rate cards via Vehicle.SpecialEquipment lookup.
      if (tailLiftValue) params.requiresTailLift = true;
      if (moffettValue) params.requiresMoffett = true;
      // Surcharge selections — each one the network prices is added to its quote total.
      if (amDeliveryValue) params.requiresAmDelivery = true;
      if (pmDeliveryValue) params.requiresPmDelivery = true;
      if (timedDeliveryValue) params.requiresTimedDelivery = true;
      if (saturdayAmDeliveryValue) params.requiresSaturdayAmDelivery = true;
      // Vehicle preferences — any non-empty value excludes pallet networks; a matching
      // PreferredVehicle also narrows LSP rate cards to the same VehicleType.
      if (preferredVehicleValue)
        params.preferredVehicle = preferredVehicleValue;
      if (curtainSideOrBoxBodyValue)
        params.curtainSideOrBoxBody = curtainSideOrBoxBodyValue;
      if (supplyChainProcessId)
        params.supplyChainProcessId = supplyChainProcessId;
      // Insurance cover (US-009) — total declared value, used to filter network services
      // whose UnitValue ceiling can't cover the per-unit value of this load.
      const insuranceNumber = Number(insuranceCoverValue);
      if (Number.isFinite(insuranceNumber) && insuranceNumber > 0) {
        params.insuranceCover = insuranceNumber;
      }
      // collectionDate is deliberately NOT sent: the fleet-availability feature is hidden,
      // so carriers no longer declare availability. Sending the date flips the backend into
      // availability strict mode, which rejects every carrier without a declared record for
      // that date and would return no LSP quotes at all. The date still ships on the order
      // itself — it just must not gate quoting until the feature returns.
      if (Number.isFinite(weightNumber) && weightNumber > 0) {
        params.weight = weightNumber;
        if (weightUnitValue) params.weightUnit = weightUnitValue;
      }
      // Per-pallet weight for the network per-pallet cap gate (PalletDimensions.Weight). The
      // form's `weight` above is the TOTAL consignment weight, which the LSP weight bands need
      // but would wrongly trip the per-pallet cap. SCP forwards prep's averagePalletWeight here;
      // standalone omits it and the backend derives per-pallet from total ÷ quantity.
      if (
        typeof averagePalletWeight === 'number' &&
        Number.isFinite(averagePalletWeight) &&
        averagePalletWeight > 0
      ) {
        params.averagePalletWeight = averagePalletWeight;
        if (weightUnitValue) params.weightUnit = weightUnitValue;
      }
      if (Number.isFinite(heightNumber) && heightNumber > 0) {
        params.height = heightNumber;
        if (heightUnitValue) params.dimensionsUnit = heightUnitValue;
      }
      // Pallet footprint — sent so the engine can filter against
      // NetworkRateCard.ServicePricing.PalletDimensions (US-009 wires the
      // backend filter).
      const lengthNumber = Number(palletLengthValue);
      const widthNumber = Number(palletWidthValue);
      if (Number.isFinite(lengthNumber) && lengthNumber > 0) {
        params.length = lengthNumber;
        if (heightUnitValue) params.dimensionsUnit = heightUnitValue;
      }
      if (Number.isFinite(widthNumber) && widthNumber > 0) {
        params.width = widthNumber;
        if (heightUnitValue) params.dimensionsUnit = heightUnitValue;
      }
      // Prefer lat/lon captured from a HERE lookup (standalone flow) over the props
      // threaded in from the despatch-note prep (SCP flow). In SCP mode the captured
      // state stays null and the props win; in standalone mode it's the other way round.
      const effectiveCollectionLat = capturedCollectionLat ?? collectionLat;
      const effectiveCollectionLon = capturedCollectionLon ?? collectionLon;
      if (effectiveCollectionLat != null && effectiveCollectionLon != null) {
        params.collectionLat = effectiveCollectionLat;
        params.collectionLon = effectiveCollectionLon;
      }
      // Delivery lat/lon also sent; backend doesn't yet consume it but it keeps the
      // contract symmetric and enables destination-proximity filtering later. Prefer a
      // HERE-captured value (standalone flow, or a user-supplied SCP postcode) over the
      // prop threaded in from the despatch-note prep — mirrors the collection side.
      const effectiveDeliveryLat = capturedDeliveryLat ?? deliveryLat;
      const effectiveDeliveryLon = capturedDeliveryLon ?? deliveryLon;
      if (effectiveDeliveryLat != null && effectiveDeliveryLon != null) {
        params.deliveryLat = effectiveDeliveryLat;
        params.deliveryLon = effectiveDeliveryLon;
      }
      const { data } = await authHttp.get<TransportQuotesResponse>(
        TRANSPORT_ORDER_QUOTES,
        { params }
      );
      if (isLatestQuoteRequest())
        setQuotes(
          withoutCourierQuotesWhenCourierCannotTakeLoad(
            data?.quotes ?? [],
            loadSize
          )
        );
    } catch (err) {
      console.error('Failed to fetch quotes:', err);
      if (isLatestQuoteRequest()) {
        setQuotes([]);
        setQuotesError('Failed to load quotes. Please try again.');
      }
    } finally {
      if (isLatestQuoteRequest()) setIsLoadingQuotes(false);
    }
  }, [
    collectionPostcode,
    deliveryPostcode,
    totalPalletsValue,
    palletTypeValue,
    weightValue,
    weightUnitValue,
    heightValue,
    heightUnitValue,
    palletLengthValue,
    palletWidthValue,
    collectionLat,
    collectionLon,
    deliveryLat,
    deliveryLon,
    averagePalletWeight,
    capturedCollectionLat,
    capturedCollectionLon,
    capturedDeliveryLat,
    capturedDeliveryLon,
    tailLiftValue,
    moffettValue,
    amDeliveryValue,
    pmDeliveryValue,
    timedDeliveryValue,
    saturdayAmDeliveryValue,
    insuranceCoverValue,
    preferredVehicleValue,
    curtainSideOrBoxBodyValue,
    supplyChainProcessId,
    loadSize,
    authHttp,
  ]);

  // Single debounced trigger that feeds both the step 1 RHS bucket summary and the
  // step 3 quote cards. Fires on any load/service input change once the minimum
  // inputs are present (fetchQuotes itself guards on them). No step gating — users
  // on step 1 should see live bucket data as they fill the form.
  useEffect(() => {
    const handle = setTimeout(() => {
      fetchQuotes();
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    collectionPostcode,
    deliveryPostcode,
    totalPalletsValue,
    palletTypeValue,
    weightValue,
    weightUnitValue,
    heightValue,
    heightUnitValue,
    palletLengthValue,
    palletWidthValue,
    tailLiftValue,
    moffettValue,
    amDeliveryValue,
    pmDeliveryValue,
    timedDeliveryValue,
    saturdayAmDeliveryValue,
    insuranceCoverValue,
    averagePalletWeight,
    preferredVehicleValue,
    curtainSideOrBoxBodyValue,
    supplyChainProcessId,
    // A postcode-area pick can set the same postcode string the user already
    // typed — the captured centroid is then the only signal a re-quote is due.
    capturedCollectionLat,
    capturedCollectionLon,
    capturedDeliveryLat,
    capturedDeliveryLon,
  ]);

  // Watch address fields for Step 1 subtitles
  const pickupCompanyName = watch('pickupCompanyName');
  const pickupFullAddress = watch('pickupFullAddress');
  const dropoffCompanyName = watch('dropoffCompanyName');
  const dropoffFullAddress = watch('dropoffFullAddress');

  // Enhanced Liability Cover watched value (Step 1)
  const fragileHandling = watch('fragileHandling');

  // Address autosuggest state. Suggestions AND the transient input mirrors are
  // kept per field — the step 1 postcode inputs and the step 2 address inputs
  // must never trade options or display text: a postcode-area row surfacing
  // inside "Full address" would erase the address when clicked, and a shared
  // mirror made the postcode field show full addresses (and vice versa).
  const [suggestionsByTarget, setSuggestionsByTarget] =
    useState(emptySuggestionState);
  const [searchStateByTarget, setSearchStateByTarget] =
    useState(idleSearchState);
  const [pickupPostcodeInput, setPickupPostcodeInput] = useState<string | null>(
    null
  );
  const [pickupAddressInput, setPickupAddressInput] = useState<string | null>(
    null
  );
  const [dropoffPostcodeInput, setDropoffPostcodeInput] = useState<
    string | null
  >(null);
  const [dropoffAddressInput, setDropoffAddressInput] = useState<string | null>(
    null
  );
  const [pickupAreaNote, setPickupAreaNote] = useState<string | null>(null);
  const [dropoffAreaNote, setDropoffAreaNote] = useState<string | null>(null);
  const latestSuggestionInput = useRef<Record<SuggestionTarget, string>>({
    pickupPostcode: '',
    dropoffPostcode: '',
    pickupAddress: '',
    dropoffAddress: '',
  });
  const suggestionDebounceHandles = useRef<
    Record<SuggestionTarget, ReturnType<typeof setTimeout> | undefined>
  >({
    pickupPostcode: undefined,
    dropoffPostcode: undefined,
    pickupAddress: undefined,
    dropoffAddress: undefined,
  });

  useEffect(
    () => () => {
      Object.values(suggestionDebounceHandles.current).forEach(clearTimeout);
    },
    []
  );

  // The postcode fields merge two sources: internal Postcodes/Resolve prefix
  // candidates ("B12 — Birmingham", from 2 chars) and HERE address autosuggest
  // (from 3 chars). HERE alone cannot serve short outward-code inputs — it
  // returns 0 results for bare outward codes, so "B12" would only ever match
  // London buildings named B12x. The step 2 address fields stay HERE-only: a
  // postcode area is not a deliverable address. Fetches are debounced because
  // authHttp requests run through an app-wide serial queue — per-keystroke
  // resolves would stall the quote fetch behind them.
  const handleFetchSuggestions = (input: string, target: SuggestionTarget) => {
    const includePostcodeAreas =
      target === 'pickupPostcode' || target === 'dropoffPostcode';
    latestSuggestionInput.current[target] = input;
    clearTimeout(suggestionDebounceHandles.current[target]);
    const isCurrent = () => latestSuggestionInput.current[target] === input;
    const setOptionsFor = (options: TransportSuggestionOption[]) =>
      setSuggestionsByTarget((prev) => ({ ...prev, [target]: options }));
    const setSearchStateFor = (state: SuggestionSearchState) =>
      setSearchStateByTarget((prev) => ({ ...prev, [target]: state }));
    const minLength = includePostcodeAreas ? MIN_POSTCODE_AREA_QUERY_LENGTH : 3;
    if (!input || input.length < minLength) {
      setOptionsFor([]);
      setSearchStateFor('idle');
      return;
    }
    setSearchStateFor('searching');
    suggestionDebounceHandles.current[target] = setTimeout(() => {
      const areasPromise = includePostcodeAreas
        ? fetchPostcodeAreaOptions(authHttp, input)
        : Promise.resolve([]);
      const addressesPromise =
        input.length >= 3
          ? fetchAddressSuggestions(input)
          : Promise.resolve([] as AddressSuggestionOption[]);
      // The internal resolve rides the app-wide serial request queue and can
      // sit behind an in-flight quote fetch; paint the HERE rows as soon as
      // they land so the wait never reads as an empty result.
      let isMergeComplete = false;
      addressesPromise.then((addressOptions) => {
        if (isMergeComplete || !isCurrent()) return;
        if (addressOptions.length > 0) setOptionsFor(addressOptions);
      });
      Promise.all([areasPromise, addressesPromise]).then(
        ([areaOptions, addressOptions]) => {
          isMergeComplete = true;
          if (!isCurrent()) return;
          const merged = mergeSuggestionSections(areaOptions, addressOptions);
          setOptionsFor(merged);
          setSearchStateFor(merged.length === 0 ? 'empty' : 'results');
        }
      );
    }, SUGGESTION_DEBOUNCE_MS);
  };

  const handlePickupSelect = async (value: any) => {
    if (!value?.id) return;
    if (value.postcodeArea) {
      // Null both mirrors so each field re-derives from form state: this field
      // shows the postcode just set, and step 2's address input keeps showing
      // the stored address — not the postcode.
      setPickupPostcodeInput(null);
      setPickupAddressInput(null);
      setValue('collectionPostcode', value.postcodeArea);
      setValue('pickupPostcode', value.postcodeArea);
      // An area pick invalidates any previously selected precise address. In the
      // despatch-note flow the address fields are locked to the note's data, so
      // only the missing postcode is filled in.
      if (!isFromDespatchNote) {
        setValue('pickupFullAddress', '');
        setValue('pickupCompanyName', '');
        setPickupAreaNote(
          `Pricing the ${value.postcodeArea} area — add the full collection address at the next step.`
        );
      }
      setCapturedCollectionLat(Number.isFinite(value.lat) ? value.lat : null);
      setCapturedCollectionLon(Number.isFinite(value.lon) ? value.lon : null);
      return;
    }
    setPickupAddressInput(value.label);
    setPickupPostcodeInput(null);
    setPickupAreaNote(null);
    setValue('pickupFullAddress', value.label);
    try {
      const response = await fetch(
        `https://lookup.search.hereapi.com/v1/lookup?id=${value.id}&apiKey=${HERE_APIKEY}`
      );
      const data = await response.json();
      if (data?.position) {
        setValue('collectionPostcode', data.address?.postalCode || '');
        setValue('pickupPostcode', data.address?.postalCode || '');
        setCapturedCollectionLat(data.position.lat ?? null);
        setCapturedCollectionLon(data.position.lng ?? null);
        // If HERE returns a named place (business, POI) whose title differs from the
        // formatted address, preserve it as the collection company name.
        if (data.title && data.title !== data.address?.label) {
          setValue('pickupCompanyName', data.title);
        }
      }
    } catch (error) {
      console.error('Error fetching pickup position:', error);
    }
  };

  const handleDropoffSelect = async (value: any) => {
    if (!value?.id) return;
    if (value.postcodeArea) {
      setDropoffPostcodeInput(null);
      setDropoffAddressInput(null);
      setValue('deliveryPostcode', value.postcodeArea);
      setValue('dropoffPostcode', value.postcodeArea);
      if (!isFromDespatchNote) {
        setValue('dropoffFullAddress', '');
        setValue('dropoffCompanyName', '');
        setDropoffAreaNote(
          `Pricing the ${value.postcodeArea} area — add the full delivery address at the next step.`
        );
      }
      setCapturedDeliveryLat(Number.isFinite(value.lat) ? value.lat : null);
      setCapturedDeliveryLon(Number.isFinite(value.lon) ? value.lon : null);
      return;
    }
    setDropoffAddressInput(value.label);
    setDropoffPostcodeInput(null);
    setDropoffAreaNote(null);
    setValue('dropoffFullAddress', value.label);
    try {
      const response = await fetch(
        `https://lookup.search.hereapi.com/v1/lookup?id=${value.id}&apiKey=${HERE_APIKEY}`
      );
      const data = await response.json();
      if (data?.position) {
        setValue('deliveryPostcode', data.address?.postalCode || '');
        setValue('dropoffPostcode', data.address?.postalCode || '');
        setCapturedDeliveryLat(data.position.lat ?? null);
        setCapturedDeliveryLon(data.position.lng ?? null);
        if (data.title && data.title !== data.address?.label) {
          setValue('dropoffCompanyName', data.title);
        }
      }
    } catch (error) {
      console.error('Error fetching dropoff position:', error);
    }
  };

  // Step 2 state — selection lives on the RHF form so it flows through onSubmit
  // and can be read by later steps. `selectedQuote` here is just a local alias for
  // readability; the authoritative value is `watch('selectedRateCardId')`.
  const selectedQuote = watch('selectedRateCardId') || null;
  const selectedQuoteServiceName = watch('selectedQuoteServiceName') || '';
  const selectedQuoteVehicleType = watch('selectedQuoteVehicleType') || '';
  const [quoteStalePriceMessage, setQuoteStalePriceMessage] = useState<
    string | null
  >(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Commit a picked quote to form state. Captures the fields the backend needs
  // for audit (US-... persist selected quote) plus a timestamp and a small JSON
  // snapshot of the rate-card pricing signal.
  const handleQuoteSelection = useCallback(
    (quote: TransportQuote) => {
      setValue('selectedRateCardId', quote.rateCardId);
      setValue('selectedQuoteSource', quote.source || '');
      setValue('selectedQuoteServiceName', quote.serviceName || '');
      setValue('selectedQuoteVehicleType', quote.vehicleType || '');
      setValue(
        'quotedPrice',
        typeof quote.price === 'number' ? quote.price : null
      );
      setValue('quotedCurrency', quote.currency || '');
      setValue('quotedAt', new Date().toISOString());
      // Snapshot is intentionally small: the fields we'd want in an audit log if
      // the underlying card is later edited. Not parsed server-side today.
      const snapshot = {
        source: quote.source,
        serviceName: quote.serviceName,
        vehicleType: quote.vehicleType,
        rateCardId: quote.rateCardId,
        price: quote.price,
        currency: quote.currency,
      };
      setValue('quotedRateCardSnapshot', JSON.stringify(snapshot));
      clearErrors('selectedRateCardId');
      setQuoteStalePriceMessage(null);
    },
    [setValue, clearErrors]
  );

  const clearQuoteSelection = useCallback(() => {
    setValue('selectedRateCardId', '');
    setValue('selectedQuoteSource', '');
    setValue('selectedQuoteServiceName', '');
    setValue('selectedQuoteVehicleType', '');
    setValue('quotedPrice', null);
    setValue('quotedCurrency', '');
    setValue('quotedAt', '');
    setValue('quotedRateCardSnapshot', '');
  }, [setValue]);

  useEffect(() => {
    if (isLoadingQuotes || quotesError) return;
    const currentId = getValues('selectedRateCardId');
    if (!currentId) return;
    const currentServiceName = getValues('selectedQuoteServiceName');
    const currentVehicleType = getValues('selectedQuoteVehicleType');
    const stillVisible = isSelectedQuoteVisibleAfterFilterChange(
      quotes,
      shipmentMethodValue,
      serviceLevelFilter,
      currentId,
      currentServiceName,
      currentVehicleType
    );
    if (!stillVisible) {
      clearQuoteSelection();
      setQuoteStalePriceMessage(
        'Your selected quote is no longer in the list — please pick again.'
      );
      return;
    }
    const visibleQuotes = filterQuotesByServiceLevel(
      quotes,
      shipmentMethodValue as ShipmentMethodBucket,
      serviceLevelFilter
    );
    const match = visibleQuotes.find((q) =>
      isSameQuote(q, currentId, currentServiceName, currentVehicleType)
    );
    if (!match) return;
    const previousPrice = getValues('quotedPrice');
    const newPrice = typeof match.price === 'number' ? match.price : null;
    if (previousPrice !== newPrice) {
      clearQuoteSelection();
      setQuoteStalePriceMessage(
        'The price on your selected quote changed — please pick again.'
      );
    }
  }, [
    quotes,
    isLoadingQuotes,
    quotesError,
    shipmentMethodValue,
    serviceLevelFilter,
    clearQuoteSelection,
    getValues,
  ]);

  useEffect(() => {
    if (!isQuoteSelectionRequired) clearErrors('selectedRateCardId');
  }, [isQuoteSelectionRequired, clearErrors]);

  useEffect(() => {
    if (!isShipmentMethodRequired) clearErrors('shipmentMethod');
  }, [isShipmentMethodRequired, clearErrors]);

  const handleShipmentMethodSelect = useCallback(
    (bucket: ShipmentMethodBucket) => {
      if (getValues('shipmentMethod') === bucket) return;
      setValue('shipmentMethod', bucket);
      clearQuoteSelection();
      clearErrors('shipmentMethod');
    },
    [getValues, setValue, clearQuoteSelection, clearErrors]
  );

  useEffect(() => {
    if (isLoadingQuotes || quotesError) return;
    const current = getValues('shipmentMethod');
    const next = nextShipmentMethod(quotes, current);
    if (next === current) return;
    setValue('shipmentMethod', next);
    clearQuoteSelection();
    clearErrors('shipmentMethod');
  }, [
    quotes,
    isLoadingQuotes,
    quotesError,
    getValues,
    setValue,
    clearQuoteSelection,
    clearErrors,
  ]);

  useEffect(() => {
    if (canVehicleCarryLoad(preferredVehicleValue, loadSize)) return;
    setValue('preferredVehicle', '');
  }, [preferredVehicleValue, loadSize, setValue]);

  useEffect(() => {
    if (shipmentMethodValue !== 'pallet-network' && serviceLevelFilter !== '') {
      setServiceLevelFilter('');
    }
  }, [shipmentMethodValue, serviceLevelFilter]);

  useEffect(() => {
    setValue('shownQuotes', quotes);
  }, [quotes, setValue]);

  // Helper to find selected option for Select components
  const findOption = (
    options: { label: string; value: string }[],
    value: string
  ) => options.find((opt) => opt.value === value) || null;

  // SCP prep normally locks the collection/delivery postcode from the warehouse/customer
  // record, but some records carry no structured postcode. The quote engine is postcode/
  // zone based, so a blank prepared postcode means the guard in fetchQuotes bails and no
  // quotes ever load — with the inputs hidden in SCP mode, the user can't recover. Surface
  // an editable field for just the missing leg. Latched on the prepared snapshot
  // (initialValues), NOT the live value, so the input stays mounted as the user types.
  const collectionPostcodeMissing =
    isFromDespatchNote && !initialValues?.collectionPostcode;
  const deliveryPostcodeMissing =
    isFromDespatchNote && !initialValues?.deliveryPostcode;
  const missingPostcodeLeg =
    collectionPostcodeMissing && deliveryPostcodeMissing
      ? 'collection or delivery'
      : collectionPostcodeMissing
      ? 'collection'
      : 'delivery';

  const renderStep1 = () => (
    <div className='grid grid-cols-2 gap-x-8'>
      {/* Left Column */}
      <div className='flex flex-col gap-y-6'>
        {isFromDespatchNote ? (
          <>
            <DespatchNoteSummaryPanel
              despatchNoteNumber={despatchNoteNumber}
              purchaseOrderNumber={purchaseOrderNumber}
              palletTypeWasDefaulted={palletTypeWasDefaulted}
              skuLines={skuLines}
            />
            <ScheduleAndHandlingPanel
              dispatchCutoffTime={dispatchCutoffTime}
              customerOperatingHoursFrom={customerOperatingHoursFrom}
              customerOperatingHoursTo={customerOperatingHoursTo}
            />
            {/* When the despatch note's warehouse/customer carries no postcode, the
                prepared value is blank and the quote engine has nothing to look up.
                Render an editable field for just the missing leg so the user can supply
                it — picking a suggestion also captures lat/lon via handlePickup/Dropoff. */}
            {(collectionPostcodeMissing || deliveryPostcodeMissing) && (
              <div className='flex flex-col gap-y-4'>
                <Subheader subHeader='Complete route details' />
                <div className='bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-y-4'>
                  <p className='flex items-start gap-1.5 text-xs text-gray-500'>
                    <ExclamationTriangleIcon className='mt-px h-4 w-4 flex-shrink-0 text-amber-500' />
                    <span>
                      The despatch note didn&apos;t include a{' '}
                      {missingPostcodeLeg} postcode. Add it to load quotes for
                      this route.
                    </span>
                  </p>
                  {collectionPostcodeMissing && (
                    <Controller
                      name='collectionPostcode'
                      control={control}
                      render={({ field }) => (
                        <div>
                          <Autocomplete
                            label='Collection Postcode'
                            placeholder='Type a postcode or place (e.g. B12, M1 2AB or Aston Cross)'
                            options={suggestionsByTarget.pickupPostcode}
                            value={pickupPostcodeInput ?? field.value ?? ''}
                            loading={
                              searchStateByTarget.pickupPostcode === 'searching'
                            }
                            noOptionsText={
                              searchStateByTarget.pickupPostcode === 'empty'
                                ? 'No matching postcodes or addresses'
                                : ''
                            }
                            onInputChange={(val) => {
                              setPickupPostcodeInput(val);
                              field.onChange(val);
                              setCapturedCollectionLat(null);
                              setCapturedCollectionLon(null);
                              handleFetchSuggestions(val, 'pickupPostcode');
                            }}
                            onChange={(val) => {
                              if (val) handlePickupSelect(val);
                            }}
                            isInvalid={!!formState.errors.collectionPostcode}
                            validationMessage={
                              formState.errors.collectionPostcode?.message
                            }
                          />
                          {(pickupCompanyName || pickupFullAddress) && (
                            <p className='mt-1 text-xs text-gray-400'>
                              {[pickupCompanyName, pickupFullAddress]
                                .filter(Boolean)
                                .join(' — ')}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  )}
                  {deliveryPostcodeMissing && (
                    <Controller
                      name='deliveryPostcode'
                      control={control}
                      render={({ field }) => (
                        <div>
                          <Autocomplete
                            label='Delivery Postcode'
                            placeholder='Type a postcode or place (e.g. B12, EC1M 8BY or Canary Wharf)'
                            options={suggestionsByTarget.dropoffPostcode}
                            value={dropoffPostcodeInput ?? field.value ?? ''}
                            loading={
                              searchStateByTarget.dropoffPostcode ===
                              'searching'
                            }
                            noOptionsText={
                              searchStateByTarget.dropoffPostcode === 'empty'
                                ? 'No matching postcodes or addresses'
                                : ''
                            }
                            onInputChange={(val) => {
                              setDropoffPostcodeInput(val);
                              field.onChange(val);
                              setCapturedDeliveryLat(null);
                              setCapturedDeliveryLon(null);
                              handleFetchSuggestions(val, 'dropoffPostcode');
                            }}
                            onChange={(val) => {
                              if (val) handleDropoffSelect(val);
                            }}
                            isInvalid={!!formState.errors.deliveryPostcode}
                            validationMessage={
                              formState.errors.deliveryPostcode?.message
                            }
                          />
                          {(dropoffCompanyName || dropoffFullAddress) && (
                            <p className='mt-1 text-xs text-gray-400'>
                              {[dropoffCompanyName, dropoffFullAddress]
                                .filter(Boolean)
                                .join(' — ')}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Locations Section */}
            <Subheader subHeader='Locations' />
            <div className='bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-y-4'>
              <Controller
                name='collectionPostcode'
                control={control}
                render={({ field }) => (
                  <div>
                    <HollyFieldBadge show={isHollyField('collectionPostcode')} />
                    {pickupAreaNote && (
                      <span className='mb-1 inline-flex w-fit items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200'>
                        Area pricing
                      </span>
                    )}
                    <Autocomplete
                      label='Collection Postcode'
                      placeholder='Type a postcode or place (e.g. B12, M1 2AB or Aston Cross)'
                      options={suggestionsByTarget.pickupPostcode}
                      value={pickupPostcodeInput ?? field.value ?? ''}
                      loading={
                        searchStateByTarget.pickupPostcode === 'searching'
                      }
                      noOptionsText={
                        searchStateByTarget.pickupPostcode === 'empty'
                          ? 'No matching postcodes or addresses'
                          : ''
                      }
                      onInputChange={(val) => {
                        setPickupPostcodeInput(val);
                        // Persist raw input so users can type a postcode without selecting
                        // — preserves existing submit-without-geo behaviour.
                        field.onChange(val);
                        setCapturedCollectionLat(null);
                        setCapturedCollectionLon(null);
                        setPickupAreaNote(null);
                        handleFetchSuggestions(val, 'pickupPostcode');
                      }}
                      onChange={(val) => {
                        if (val) handlePickupSelect(val);
                      }}
                      disabled={isFromDespatchNote}
                      isInvalid={!!formState.errors.collectionPostcode}
                      validationMessage={
                        formState.errors.collectionPostcode?.message
                      }
                    />
                    {pickupAreaNote ? (
                      <p className='mt-1 flex items-start gap-1 text-xs text-gray-500'>
                        <span className='text-blue-500'>ℹ</span>
                        <span>{pickupAreaNote}</span>
                      </p>
                    ) : (
                      (pickupCompanyName || pickupFullAddress) && (
                        <p className='mt-1 text-xs text-gray-500'>
                          {[pickupCompanyName, pickupFullAddress]
                            .filter(Boolean)
                            .join(' — ')}
                        </p>
                      )
                    )}
                  </div>
                )}
              />
              <Controller
                name='deliveryPostcode'
                control={control}
                render={({ field }) => (
                  <div>
                    <HollyFieldBadge show={isHollyField('deliveryPostcode')} />
                    {dropoffAreaNote && (
                      <span className='mb-1 inline-flex w-fit items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200'>
                        Area pricing
                      </span>
                    )}
                    <Autocomplete
                      label='Delivery Postcode'
                      placeholder='Type a postcode or place (e.g. B12, EC1M 8BY or Canary Wharf)'
                      options={suggestionsByTarget.dropoffPostcode}
                      value={dropoffPostcodeInput ?? field.value ?? ''}
                      loading={
                        searchStateByTarget.dropoffPostcode === 'searching'
                      }
                      noOptionsText={
                        searchStateByTarget.dropoffPostcode === 'empty'
                          ? 'No matching postcodes or addresses'
                          : ''
                      }
                      onInputChange={(val) => {
                        setDropoffPostcodeInput(val);
                        field.onChange(val);
                        setCapturedDeliveryLat(null);
                        setCapturedDeliveryLon(null);
                        setDropoffAreaNote(null);
                        handleFetchSuggestions(val, 'dropoffPostcode');
                      }}
                      onChange={(val) => {
                        if (val) handleDropoffSelect(val);
                      }}
                      disabled={isFromDespatchNote}
                      isInvalid={!!formState.errors.deliveryPostcode}
                      validationMessage={
                        formState.errors.deliveryPostcode?.message
                      }
                    />
                    {dropoffAreaNote ? (
                      <p className='mt-1 flex items-start gap-1 text-xs text-gray-500'>
                        <span className='text-blue-500'>ℹ</span>
                        <span>{dropoffAreaNote}</span>
                      </p>
                    ) : (
                      (dropoffCompanyName || dropoffFullAddress) && (
                        <p className='mt-1 text-xs text-gray-500'>
                          {[dropoffCompanyName, dropoffFullAddress]
                            .filter(Boolean)
                            .join(' — ')}
                        </p>
                      )
                    )}
                  </div>
                )}
              />
            </div>

            {/* Dimensions and Weight Section */}
            <Subheader subHeader='Dimensions and Weight' />
            <div className='bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-y-4'>
              <div
                className='grid gap-4'
                style={{ gridTemplateColumns: '1fr 2fr 1fr' }}
              >
                <Controller
                  name='totalPallets'
                  control={control}
                  render={({ field }) => (
                    <div>
                      <HollyFieldBadge show={isHollyField('totalPallets')} />
                      <Input
                        {...field}
                        value={String(field.value)}
                        label='Pallets'
                        placeholder='0'
                        sizeVariant='sm'
                        disabled={isFromDespatchNote}
                        isInvalid={!!formState.errors.totalPallets}
                        validationMessage={
                          formState.errors.totalPallets?.message
                        }
                      />
                    </div>
                  )}
                />
                <Controller
                  name='palletType'
                  control={control}
                  render={({ field }) => (
                    <div>
                      <HollyFieldBadge show={isHollyField('palletType')} />
                      <Select
                        label='Type'
                        placeholder='Please select'
                        options={palletTypeOptions}
                        selected={findOption(palletTypeOptions, field.value)}
                        setSelected={(opt) => field.onChange(opt.value)}
                        disabled={isFromDespatchNote}
                        isInvalid={!!formState.errors.palletType}
                        validationMessage={formState.errors.palletType?.message}
                      />
                    </div>
                  )}
                />
                <div>
                  <label className='block text-sm font-medium text-gray-900 font-inter mb-2'>
                    Stackable
                  </label>
                  <ToggleButtonGroup
                    options={[
                      { value: 'yes', label: 'Yes' },
                      { value: 'no', label: 'No' },
                    ]}
                    selected={stackable ? 'yes' : 'no'}
                    onChange={(value) => setValue('stackable', value === 'yes')}
                  />
                </div>
              </div>

              <Controller
                name='briefContents'
                control={control}
                render={({ field }) => (
                  <div>
                    <HollyFieldBadge show={isHollyField('briefContents')} />
                    <Textarea
                      {...field}
                      label='Brief contents description'
                      placeholder='e.g. 12 boxes of electronics, palletised'
                      isInvalid={!!formState.errors.briefContents}
                      validationMessage={
                        formState.errors.briefContents?.message
                      }
                    />
                  </div>
                )}
              />

              <div className='grid grid-cols-2 gap-4'>
                <div className='flex gap-2'>
                  <Controller
                    name='height'
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={String(field.value)}
                        label='Max Pallet Height'
                        placeholder=''
                        sizeVariant='sm'
                        disabled={isFromDespatchNote}
                        isInvalid={!!formState.errors.height}
                        validationMessage={formState.errors.height?.message}
                      />
                    )}
                  />
                  <Controller
                    name='heightUnit'
                    control={control}
                    render={({ field }) => (
                      <Select
                        label='Unit'
                        options={unitOptions}
                        selected={findOption(unitOptions, field.value)}
                        setSelected={(opt) => field.onChange(opt.value)}
                        disabled={isFromDespatchNote}
                        isInvalid={!!formState.errors.heightUnit}
                        validationMessage={formState.errors.heightUnit?.message}
                      />
                    )}
                  />
                </div>
                <div className='flex gap-2'>
                  <Controller
                    name='weight'
                    control={control}
                    render={({ field }) => (
                      <div className='flex-1'>
                        <HollyFieldBadge show={isHollyField('weight')} />
                        <Input
                          {...field}
                          value={String(field.value)}
                          label='Total Weight'
                          placeholder=''
                          sizeVariant='sm'
                          disabled={isFromDespatchNote}
                          isInvalid={!!formState.errors.weight}
                          validationMessage={formState.errors.weight?.message}
                        />
                      </div>
                    )}
                  />
                  <Controller
                    name='weightUnit'
                    control={control}
                    render={({ field }) => (
                      <Select
                        label='Unit'
                        options={weightUnitOptions}
                        selected={findOption(weightUnitOptions, field.value)}
                        setSelected={(opt) => field.onChange(opt.value)}
                        disabled={isFromDespatchNote}
                        isInvalid={!!formState.errors.weightUnit}
                        validationMessage={formState.errors.weightUnit?.message}
                      />
                    )}
                  />
                </div>
              </div>

              {palletTierExcessMessage && (
                <div className='flex items-start gap-2 rounded-md bg-amber-50/60 px-3 py-2'>
                  <ExclamationTriangleIcon className='mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500' />
                  <p className='text-xs text-gray-600'>
                    {palletTierExcessMessage}
                  </p>
                </div>
              )}

              {/* Pallet footprint (length × width). Hidden for backend-resolved pallet types
                  — the controller fills the canonical footprint, so editable inputs would
                  mislead the user into thinking their values are used. Shown (and used) for
                  Custom / Other / Unpalletised / Mixed, which the backend doesn't resolve.
                  Height is always shown separately above — it varies per load. */}
              {!isBackendResolvedPalletType(palletTypeValue) && (
                <div className='grid grid-cols-2 gap-4'>
                  <Controller
                    name='palletLength'
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={String(field.value)}
                        label={`Pallet Length${
                          heightUnitValue ? ` (${heightUnitValue})` : ''
                        }`}
                        placeholder='Required — provide the pallet dimensions'
                        sizeVariant='sm'
                        disabled={isFromDespatchNote}
                        isInvalid={!!formState.errors.palletLength}
                        validationMessage={
                          formState.errors.palletLength?.message
                        }
                      />
                    )}
                  />
                  <Controller
                    name='palletWidth'
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={String(field.value)}
                        label={`Pallet Width${
                          heightUnitValue ? ` (${heightUnitValue})` : ''
                        }`}
                        placeholder='Required — provide the pallet dimensions'
                        sizeVariant='sm'
                        disabled={isFromDespatchNote}
                        isInvalid={!!formState.errors.palletWidth}
                        validationMessage={
                          formState.errors.palletWidth?.message
                        }
                      />
                    )}
                  />
                </div>
              )}

              <div className='flex flex-col gap-y-2'>
                <label className='block text-sm font-medium text-gray-900 font-inter'>
                  Overhang beyond pallet footprint
                </label>
                <div className='flex items-center gap-4'>
                  <ToggleButtonGroup
                    options={[
                      { value: 'yes', label: 'Yes' },
                      { value: 'no', label: 'No' },
                    ]}
                    selected={overhang ? 'yes' : 'no'}
                    onChange={(value) => setValue('overhang', value === 'yes')}
                    variant={overhang ? 'warning' : 'primary'}
                  />
                  <div className='flex-1'>
                    <Controller
                      name='overhangDescription'
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          placeholder='Describe here...'
                          sizeVariant='sm'
                          name='overhangDescription'
                          disabled={!overhang}
                          isInvalid={!!formState.errors.overhangDescription}
                          validationMessage={
                            formState.errors.overhangDescription?.message
                          }
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              <Controller
                name='packaging'
                control={control}
                render={({ field }) => (
                  <Select
                    label='Packaging'
                    placeholder='Please select'
                    options={packagingOptions}
                    selected={findOption(packagingOptions, field.value)}
                    setSelected={(opt) => field.onChange(opt.value)}
                    disabled={isFromDespatchNote}
                    isInvalid={!!formState.errors.packaging}
                    validationMessage={formState.errors.packaging?.message}
                  />
                )}
              />
            </div>

            {/* Timing Section */}
            <Subheader subHeader='Timing' />
            <div className='bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-y-4'>
              <div>
                <label className='flex items-center gap-2 text-sm font-medium text-gray-900 font-inter mb-2'>
                  Collection date and time window
                  <HollyFieldBadge show={isHollyField('collectionDate')} />
                </label>
                <div className='flex items-center gap-2'>
                  <Controller
                    name='collectionDate'
                    control={control}
                    render={({ field }) => (
                      <Datepicker
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        disabled={isFromDespatchNote}
                        isInvalid={!!formState.errors.collectionDate}
                        validationMessage={
                          formState.errors.collectionDate?.message
                        }
                      />
                    )}
                  />
                  <span className='text-sm text-gray-500'>from</span>
                  <Controller
                    name='collectionTimeFrom'
                    control={control}
                    render={({ field }) => (
                      <Timepicker
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        isInvalid={!!formState.errors.collectionTimeFrom}
                        validationMessage={
                          formState.errors.collectionTimeFrom?.message
                        }
                      />
                    )}
                  />
                  <span className='text-sm text-gray-500'>to</span>
                  <Controller
                    name='collectionTimeTo'
                    control={control}
                    render={({ field }) => (
                      <Timepicker
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        isInvalid={!!formState.errors.collectionTimeTo}
                        validationMessage={
                          formState.errors.collectionTimeTo?.message
                        }
                      />
                    )}
                  />
                  <InformationCircleIcon className='h-5 w-5 text-gray-400' />
                </div>
                <p className='text-xs text-gray-500 mt-1'>
                  All times are in London time (GMT/BST)
                </p>
              </div>

              <div>
                <label className='block text-sm font-medium text-gray-900 font-inter mb-2'>
                  Delivery date and time window
                </label>
                <div className='flex items-center gap-2'>
                  <Controller
                    name='deliveryDate'
                    control={control}
                    render={({ field }) => (
                      <Datepicker
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        disabled={isFromDespatchNote}
                        isInvalid={!!formState.errors.deliveryDate}
                        validationMessage={
                          formState.errors.deliveryDate?.message
                        }
                      />
                    )}
                  />
                  <span className='text-sm text-gray-500'>from</span>
                  <Controller
                    name='deliveryTimeFrom'
                    control={control}
                    render={({ field }) => (
                      <Timepicker
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        isInvalid={!!formState.errors.deliveryTimeFrom}
                        validationMessage={
                          formState.errors.deliveryTimeFrom?.message
                        }
                      />
                    )}
                  />
                  <span className='text-sm text-gray-500'>to</span>
                  <Controller
                    name='deliveryTimeTo'
                    control={control}
                    render={({ field }) => (
                      <Timepicker
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        isInvalid={!!formState.errors.deliveryTimeTo}
                        validationMessage={
                          formState.errors.deliveryTimeTo?.message
                        }
                      />
                    )}
                  />
                  <InformationCircleIcon className='h-5 w-5 text-gray-400' />
                </div>
                <p className='text-xs text-gray-500 mt-1'>
                  All times are in London time (GMT/BST)
                </p>
              </div>

              <Controller
                name='bookingReference'
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    label='Booking reference / additional notes'
                    placeholder=''
                    disabled={isFromDespatchNote}
                  />
                )}
              />
            </div>
          </>
        )}

        {/* Surcharges Section */}
        <Subheader subHeader='Surcharges' />
        <div className='bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-y-4'>
          <Controller
            name='tailLiftRequired'
            control={control}
            render={({ field }) => (
              <div>
                <HollyFieldBadge show={isHollyField('tailLiftRequired')} />
                <ToggleSwitch
                  label='Tail lift'
                  checked={field.value}
                  onChange={field.onChange}
                />
              </div>
            )}
          />
          <Controller
            name='amDeliveryRequired'
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                label='AM delivery'
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name='pmDeliveryRequired'
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                label='PM delivery'
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name='timedDeliveryRequired'
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                label='Timed delivery'
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name='saturdayAmDeliveryRequired'
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                label='SAT AM delivery'
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <p className='text-xs text-gray-500 flex items-start gap-1'>
            <span className='text-blue-500'>ℹ</span>
            Prices on the right update as you pick surcharges. Carriers that
            don&apos;t charge for one add nothing for it.
          </p>
        </div>

        <Subheader subHeader='General haulage / courier' />
        <div className='bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-y-4'>
          <div className='grid grid-cols-2 gap-4'>
            <Controller
              name='preferredVehicle'
              control={control}
              render={({ field }) => (
                <Select
                  label='Preferred vehicle'
                  placeholder='Please select'
                  options={vehicleOptionsForTheLoad}
                  selected={findOption(vehicleOptionsForTheLoad, field.value)}
                  setSelected={(opt) => field.onChange(opt.value)}
                  isInvalid={!!formState.errors.preferredVehicle}
                  validationMessage={formState.errors.preferredVehicle?.message}
                />
              )}
            />
            <Controller
              name='curtainSideOrBoxBody'
              control={control}
              render={({ field }) => (
                <Select
                  label='Curtain-side or box body'
                  placeholder='Please select'
                  options={curtainBoxOptions}
                  selected={findOption(curtainBoxOptions, field.value)}
                  setSelected={(opt) => field.onChange(opt.value)}
                  isInvalid={!!formState.errors.curtainSideOrBoxBody}
                  validationMessage={
                    formState.errors.curtainSideOrBoxBody?.message
                  }
                />
              )}
            />
          </div>

          <div className='grid grid-cols-2 gap-4'>
            <Controller
              name='moffettRequired'
              control={control}
              render={({ field }) => (
                <div>
                  <HollyFieldBadge show={isHollyField('moffettRequired')} />
                  <Select
                    label='Moffett / forklift required'
                    options={yesNoOptions}
                    selected={findOption(
                      yesNoOptions,
                      field.value ? 'yes' : 'no'
                    )}
                    setSelected={(opt) => field.onChange(opt.value === 'yes')}
                    isInvalid={!!formState.errors.moffettRequired}
                    validationMessage={
                      formState.errors.moffettRequired?.message
                    }
                  />
                </div>
              )}
            />
          </div>

          {isDedicatedVehicleRequired && (
            <div className='flex items-start gap-2 rounded-md bg-amber-50/60 px-3 py-2'>
              <ExclamationTriangleIcon className='mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500' />
              <p className='text-xs text-gray-600'>
                These options need a dedicated vehicle, which the shared pallet
                networks can&apos;t provide, so pallet-network quotes are hidden
                while any of these is set. Set them back to &quot;Any&quot; or
                &quot;No&quot; to bring pallet-network quotes back.
              </p>
            </div>
          )}
        </div>

        {/* Loading/Unloading on Site Section */}
        <Subheader subHeader='Loading / Unloading on Site' />
        <div className='bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-y-4'>
          <Controller
            name='forkliftAtCollection'
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                label='Forklift available at collection'
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name='forkliftAtDelivery'
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                label='Forklift available at delivery'
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name='dockLoading'
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                label='Dock loading'
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name='handballRequired'
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                label='Handball required'
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name='palletTruckNeeded'
            control={control}
            render={({ field }) => (
              <ToggleSwitch
                label='Pallet truck needed'
                checked={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>

        {/* Enhanced Liability Cover Section */}
        <Subheader subHeader='Enhanced Liability Cover' />
        <div className='bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-y-4'>
          <div className='flex flex-col gap-y-2'>
            <label className='flex items-center gap-2 text-sm font-medium text-gray-900 font-inter'>
              Fragile / high-value handling
              {autoSetFlags?.fragile && (
                <span
                  className='inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-200'
                  title='Auto-set from SKU MetaData (fragile). Toggle off if incorrect.'
                >
                  Auto-set from SKU
                </span>
              )}
              <HollyFieldBadge show={isHollyField('fragileHandling')} />
            </label>
            <ToggleButtonGroup
              options={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
              ]}
              selected={fragileHandling ? 'yes' : 'no'}
              onChange={(value) => setValue('fragileHandling', value === 'yes')}
              variant={fragileHandling ? 'warning' : 'primary'}
            />
          </div>

          <div className='grid grid-cols-2 gap-4'>
            <Controller
              name='insuranceCover'
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label='Insurance cover required'
                  placeholder='e.g. £50,000'
                  sizeVariant='sm'
                  isInvalid={!!formState.errors.insuranceCover}
                  validationMessage={formState.errors.insuranceCover?.message}
                />
              )}
            />
            <Controller
              name='insuranceCurrency'
              control={control}
              render={({ field }) => (
                <Select
                  label='Currency'
                  options={currencyOptions}
                  selected={findOption(currencyOptions, field.value)}
                  setSelected={(opt) => field.onChange(opt.value)}
                  isInvalid={!!formState.errors.insuranceCurrency}
                  validationMessage={
                    formState.errors.insuranceCurrency?.message
                  }
                />
              )}
            />
          </div>
        </div>
      </div>

      {/* Right Column - Carrier and Shipment Method */}
      <div className='flex flex-col gap-y-6 sticky top-32 self-start'>
        <Subheader subHeader='Shipment Methods' />
        <fieldset
          name='shipmentMethod'
          tabIndex={-1}
          className='bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-y-4 focus:outline-none'
        >
          <div className='flex flex-col gap-y-3'>
            {formState.errors.shipmentMethod && (
              <p className='text-sm text-red-600'>
                {formState.errors.shipmentMethod.message}
              </p>
            )}
            {quotesError && (
              <div className='flex flex-col items-center gap-3 rounded-lg border-2 border-amber-100 bg-amber-50 p-4 text-center'>
                <ExclamationTriangleIcon className='h-7 w-7 flex-shrink-0 text-amber-500' />
                <p className='text-sm text-gray-700'>{quotesError}</p>
                <button
                  type='button'
                  onClick={() => fetchQuotes()}
                  className='px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90'
                >
                  Retry
                </button>
              </div>
            )}
            {(quotesError ? [] : shipmentMethodBuckets).map((method) => {
              if (isLoadingQuotes) {
                return (
                  <div
                    key={method.id}
                    className='w-full rounded-lg border-2 border-gray-200 bg-white p-4'
                  >
                    <div className='flex items-start gap-3'>
                      <div className='h-8 w-8 flex-shrink-0 rounded bg-gray-200 animate-pulse' />
                      <div className='flex-1'>
                        <div className='h-4 w-32 rounded bg-gray-200 animate-pulse' />
                        <div className='mt-2 h-3 w-48 rounded bg-gray-100 animate-pulse' />
                        <div className='mt-3 h-4 w-24 rounded bg-gray-200 animate-pulse' />
                      </div>
                    </div>
                  </div>
                );
              }
              const bucketQuotes = quotes.filter(
                (q) => bucketForSource(q.source) === method.id
              );
              const bucketDisplay = describeShipmentMethodBucket(
                method.id,
                quotes,
                weightValue,
                isFromDespatchNote
              );
              const showPalletNetworkExclusion =
                method.id === 'pallet-network' &&
                isDedicatedVehicleRequired &&
                bucketQuotes.length === 0;
              return (
                <div key={method.id} className='flex flex-col gap-y-2'>
                  <ShipmentMethodCard
                    title={method.title}
                    description={method.description}
                    price={bucketDisplay.summary}
                    selected={shipmentMethodValue === method.id}
                    readOnly={!bucketDisplay.isSelectable}
                    onClick={() => handleShipmentMethodSelect(method.id)}
                    icon={
                      method.id === 'warehouse-lsp' ? (
                        <BuildingOfficeIcon className='h-8 w-8' />
                      ) : method.id === 'courier' ? (
                        <BoltIcon className='h-8 w-8' />
                      ) : (
                        <CubeIcon className='h-8 w-8' />
                      )
                    }
                  />
                  {showPalletNetworkExclusion && (
                    <p className='flex items-start gap-1.5 px-1 text-xs text-gray-500'>
                      <ExclamationTriangleIcon className='mt-px h-4 w-4 flex-shrink-0 text-amber-500' />
                      <span>
                        Your General haulage / courier options call for a
                        dedicated vehicle, which the pallet networks can&apos;t
                        provide. Reset them to &quot;Any&quot; or &quot;No&quot;
                        to compare pallet-network rates.
                      </span>
                    </p>
                  )}
                </div>
              );
            })}

            {!canCourierTakeLoad(loadSize) && (
              <ShipmentMethodCard
                title='General haulage'
                description='Full and part loads moved by a dedicated haulier.'
                price='POA'
                readOnly
                icon={<TruckIcon className='h-8 w-8' />}
              />
            )}
          </div>

          <p className='text-xs text-gray-500 flex items-start gap-1'>
            <span className='text-blue-500'>ℹ</span>
            Indicative prices update as you fill in load details. Your final
            quote is confirmed when you submit the order.
          </p>
          {!supplyChainProcessId && (
            <p className='text-xs text-gray-500 flex items-start gap-1'>
              <span className='text-blue-500'>ℹ</span>
              Your contracted warehouse&apos;s own LSP rates show when you start
              the order from a despatch note.
            </p>
          )}
        </fieldset>
      </div>
    </div>
  );

  const renderStep2 = () => {
    return (
      <div className='mx-auto flex w-full max-w-5xl flex-col gap-y-8'>
        {/* Carrier selection — kept above the addresses so the quote cards aren't
            buried below the long address form. */}
        <fieldset
          name='selectedRateCardId'
          tabIndex={-1}
          className='min-w-0 focus:outline-none'
        >
          {formState.errors.selectedRateCardId && (
            <p className='mb-2 text-sm text-red-600'>
              {formState.errors.selectedRateCardId.message}
            </p>
          )}
          {/* eslint-disable-next-line @typescript-eslint/no-use-before-define -- renderQuoteSelection is a co-located step-2 helper defined just below; both run from renderCurrentStep at render time. */}
          {renderQuoteSelection()}
        </fieldset>

        {/* From → To */}
        <div className='grid grid-cols-[1fr_auto_1fr] items-center gap-4'>
          {/* Collection Address (From) */}
          <div className='flex flex-col gap-y-4'>
            <div className='flex items-center justify-between'>
              <Subheader subHeader='Collection Address' />
              {isFromDespatchNote && (
                <span
                  className='inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-200'
                  title='Company, address and postcode are sourced from the despatch note and locked. Contacts and notes remain editable.'
                >
                  Sourced from despatch note
                </span>
              )}
            </div>
            <div className='bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-y-4'>
              <Controller
                name='pickupCompanyName'
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label='Company name'
                    placeholder=''
                    sizeVariant='sm'
                    disabled={isFromDespatchNote}
                    isInvalid={!!formState.errors.pickupCompanyName}
                    validationMessage={
                      formState.errors.pickupCompanyName?.message
                    }
                  />
                )}
              />

              <Controller
                name='pickupFullAddress'
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    label='Full address'
                    placeholder='Start typing to search...'
                    options={suggestionsByTarget.pickupAddress}
                    value={pickupAddressInput ?? field.value}
                    loading={searchStateByTarget.pickupAddress === 'searching'}
                    noOptionsText={
                      searchStateByTarget.pickupAddress === 'empty'
                        ? 'No matching addresses'
                        : ''
                    }
                    onInputChange={(val) => {
                      setPickupAddressInput(val);
                      field.onChange(val);
                      handleFetchSuggestions(val, 'pickupAddress');
                    }}
                    onChange={(val) => handlePickupSelect(val)}
                    disabled={isFromDespatchNote}
                    isInvalid={!!formState.errors.pickupFullAddress}
                    validationMessage={
                      formState.errors.pickupFullAddress?.message as string
                    }
                  />
                )}
              />

              <Controller
                name='pickupPostcode'
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label='Postcode'
                    placeholder=''
                    sizeVariant='sm'
                    disabled={isFromDespatchNote}
                    isInvalid={!!formState.errors.pickupPostcode}
                    validationMessage={formState.errors.pickupPostcode?.message}
                  />
                )}
              />

              <Controller
                name='pickupContactName'
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label='Contact name'
                    placeholder=''
                    sizeVariant='sm'
                    isInvalid={!!formState.errors.pickupContactName}
                    validationMessage={
                      formState.errors.pickupContactName?.message
                    }
                  />
                )}
              />

              <Controller
                name='pickupContactNumber'
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label='Contact number'
                    placeholder=''
                    sizeVariant='sm'
                    isInvalid={!!formState.errors.pickupContactNumber}
                    validationMessage={
                      formState.errors.pickupContactNumber?.message
                    }
                  />
                )}
              />

              <Controller
                name='pickupContactEmail'
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label='Contact email'
                    placeholder=''
                    sizeVariant='sm'
                    isInvalid={!!formState.errors.pickupContactEmail}
                    validationMessage={
                      formState.errors.pickupContactEmail?.message
                    }
                  />
                )}
              />

              <Controller
                name='pickupAdditionalNotes'
                control={control}
                render={({ field }) => (
                  <div>
                    <HollyFieldBadge
                      show={isHollyField('pickupAdditionalNotes')}
                    />
                    <Textarea
                      {...field}
                      label='Additional notes'
                      placeholder='Notes, info, restrictions...'
                      isInvalid={!!formState.errors.pickupAdditionalNotes}
                      validationMessage={
                        formState.errors.pickupAdditionalNotes?.message
                      }
                    />
                  </div>
                )}
              />
            </div>
          </div>

          {/* Route arrow between the two address columns */}
          <ArrowLongRightIcon className='h-7 w-7 flex-shrink-0 text-gray-300' />

          {/* Delivery Address (To) */}
          <div className='flex flex-col gap-y-4'>
            <div className='flex items-center justify-between'>
              <Subheader subHeader='Delivery Address' />
              {isFromDespatchNote && (
                <span
                  className='inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-200'
                  title='Company, address and postcode are sourced from the despatch note and locked. Contacts and notes remain editable.'
                >
                  Sourced from despatch note
                </span>
              )}
            </div>
            <div className='bg-white rounded-lg border border-gray-200 p-4 flex flex-col gap-y-4'>
              <Controller
                name='dropoffCompanyName'
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label='Company name'
                    placeholder=''
                    sizeVariant='sm'
                    disabled={isFromDespatchNote}
                    isInvalid={!!formState.errors.dropoffCompanyName}
                    validationMessage={
                      formState.errors.dropoffCompanyName?.message
                    }
                  />
                )}
              />

              <Controller
                name='dropoffFullAddress'
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    label='Full address'
                    placeholder='Start typing to search...'
                    options={suggestionsByTarget.dropoffAddress}
                    value={dropoffAddressInput ?? field.value}
                    loading={searchStateByTarget.dropoffAddress === 'searching'}
                    noOptionsText={
                      searchStateByTarget.dropoffAddress === 'empty'
                        ? 'No matching addresses'
                        : ''
                    }
                    onInputChange={(val) => {
                      setDropoffAddressInput(val);
                      field.onChange(val);
                      handleFetchSuggestions(val, 'dropoffAddress');
                    }}
                    onChange={(val) => handleDropoffSelect(val)}
                    disabled={isFromDespatchNote}
                    isInvalid={!!formState.errors.dropoffFullAddress}
                    validationMessage={
                      formState.errors.dropoffFullAddress?.message as string
                    }
                  />
                )}
              />

              <Controller
                name='dropoffPostcode'
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label='Postcode'
                    placeholder=''
                    sizeVariant='sm'
                    disabled={isFromDespatchNote}
                    isInvalid={!!formState.errors.dropoffPostcode}
                    validationMessage={
                      formState.errors.dropoffPostcode?.message
                    }
                  />
                )}
              />

              <Controller
                name='dropoffContactName'
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label='Contact name'
                    placeholder=''
                    sizeVariant='sm'
                    isInvalid={!!formState.errors.dropoffContactName}
                    validationMessage={
                      formState.errors.dropoffContactName?.message
                    }
                  />
                )}
              />

              <Controller
                name='dropoffContactNumber'
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label='Contact number'
                    placeholder=''
                    sizeVariant='sm'
                    isInvalid={!!formState.errors.dropoffContactNumber}
                    validationMessage={
                      formState.errors.dropoffContactNumber?.message
                    }
                  />
                )}
              />

              <Controller
                name='dropoffContactEmail'
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label='Contact email'
                    placeholder=''
                    sizeVariant='sm'
                    isInvalid={!!formState.errors.dropoffContactEmail}
                    validationMessage={
                      formState.errors.dropoffContactEmail?.message
                    }
                  />
                )}
              />

              <Controller
                name='dropoffAdditionalNotes'
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    label='Additional notes'
                    placeholder='Notes, info, restrictions...'
                    isInvalid={!!formState.errors.dropoffAdditionalNotes}
                    validationMessage={
                      formState.errors.dropoffAdditionalNotes?.message
                    }
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderQuoteSelection = () => {
    const showServiceLevelFilter = shipmentMethodValue === 'pallet-network';

    const serviceLevelSelect = (
      <div className='w-44'>
        <Select
          label='Service level'
          options={serviceLevelOptions}
          selected={findOption(serviceLevelOptions, serviceLevelFilter)}
          setSelected={(option) => setServiceLevelFilter(String(option.value))}
        />
      </div>
    );

    if (isLoadingQuotes) {
      return (
        <div className='flex flex-col items-center py-8'>
          {showServiceLevelFilter && (
            <div className='mb-4'>{serviceLevelSelect}</div>
          )}
          <div className='mb-6'>
            <svg
              className='w-16 h-16 text-primary'
              viewBox='0 0 64 64'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <circle
                cx='28'
                cy='28'
                r='12'
                stroke='currentColor'
                strokeWidth='2'
              />
              <path
                d='M37 37L45 45'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
              />
              <path
                d='M20 28H36'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
              />
              <path
                d='M28 20V36'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
              />
              <circle
                cx='50'
                cy='20'
                r='8'
                stroke='currentColor'
                strokeWidth='2'
              />
              <path
                d='M47 20H53'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
              />
              <path
                d='M50 17V23'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
              />
            </svg>
          </div>

          <h2 className='text-lg font-semibold text-gray-900 mb-2'>
            Searching quotes and building routes...
          </h2>
          <p className='text-sm text-gray-500 text-center max-w-md mb-8'>
            We are identifying the best carrier with an optimal route schedule.
            Kindly wait as we continuously gather carrier availability
            information.
          </p>

          <div className='flex flex-wrap justify-center gap-3 pt-3'>
            {[1, 2, 3, 4].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        </div>
      );
    }

    if (quotesError) {
      return (
        <div className='flex flex-col items-center py-12 gap-4'>
          <p className='text-gray-500'>{quotesError}</p>
          <button
            type='button'
            onClick={() => fetchQuotes()}
            className='px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90'
          >
            Retry
          </button>
        </div>
      );
    }

    const methodSwitcher = (
      <div className='flex flex-wrap gap-2'>
        {shipmentMethodBuckets.map((bucket) => {
          const display = describeShipmentMethodBucket(
            bucket.id,
            quotes,
            weightValue,
            isFromDespatchNote
          );
          const isChosen = shipmentMethodValue === bucket.id;
          return (
            <button
              key={bucket.id}
              type='button'
              disabled={!display.isSelectable}
              onClick={() => handleShipmentMethodSelect(bucket.id)}
              className={
                isChosen
                  ? 'flex flex-col items-start gap-0.5 rounded-lg border-2 border-primary bg-blue-50 px-3 py-2 text-left text-xs'
                  : display.isSelectable
                  ? 'flex flex-col items-start gap-0.5 rounded-lg border-2 border-gray-200 bg-white px-3 py-2 text-left text-xs text-gray-700 hover:border-gray-300'
                  : 'flex flex-col items-start gap-0.5 rounded-lg border-2 border-gray-100 bg-gray-50 px-3 py-2 text-left text-xs text-gray-400 cursor-not-allowed'
              }
            >
              <span className='font-medium text-gray-900'>{bucket.title}</span>
              <span>{display.summary}</span>
            </button>
          );
        })}
      </div>
    );

    const bothPostcodesSet = !!collectionPostcode && !!deliveryPostcode;
    const noQuotesEmptyState = (message: string) => (
      <div className='flex flex-col items-center py-16 gap-3'>
        <div className='w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center'>
          <svg
            className='w-7 h-7 text-amber-500'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
            strokeWidth={1.5}
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z'
            />
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M12 2.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5z'
            />
          </svg>
        </div>
        <h2 className='text-lg font-semibold text-gray-900'>
          No matching quotes available
        </h2>
        {bothPostcodesSet ? (
          <>
            <p className='text-sm text-gray-500 text-center max-w-md'>
              {message}
            </p>
            <p className='text-xs text-gray-400 font-mono'>
              {collectionPostcode} &rarr; {deliveryPostcode}
            </p>
            <div className='mt-2 flex flex-wrap items-end justify-center gap-3'>
              {showServiceLevelFilter && serviceLevelSelect}
              <button
                type='button'
                onClick={() => fetchQuotes()}
                className='px-4 py-2 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-blue-50'
              >
                Retry
              </button>
            </div>
          </>
        ) : (
          <p className='text-sm text-gray-500 text-center max-w-md'>
            Collection or delivery postcode is missing — go back to step 1 and
            complete the route details to fetch quotes.
          </p>
        )}
      </div>
    );

    const noMatchingQuotesCopy =
      "We couldn't find any matching quotes for this route. You can still continue and submit the order — our carrier-ops team will pick it up and send a manual quote.";

    const staleQuoteNotice = quoteStalePriceMessage && (
      <div className='flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3'>
        <span className='text-amber-500 mt-0.5'>⚠</span>
        <div className='flex-1 text-sm text-amber-800'>
          {quoteStalePriceMessage}
        </div>
        <button
          type='button'
          onClick={() => setQuoteStalePriceMessage(null)}
          className='text-xs text-amber-700 hover:text-amber-900'
        >
          Dismiss
        </button>
      </div>
    );

    if (!shipmentMethodValue) {
      if (quotes.length === 0) {
        return (
          <div className='flex flex-col gap-6'>
            {methodSwitcher}
            {staleQuoteNotice}
            {noQuotesEmptyState(noMatchingQuotesCopy)}
          </div>
        );
      }
      return (
        <div className='flex flex-col gap-6'>
          <div className='flex flex-col items-center gap-2 text-center'>
            <h2 className='text-lg font-semibold text-gray-900'>
              Choose a shipment method
            </h2>
            <p className='text-sm text-gray-500 max-w-md'>
              Choose a shipment method below to see its quotes.
            </p>
          </div>
          {methodSwitcher}
          {staleQuoteNotice}
        </div>
      );
    }

    const visibleQuotes = filterQuotesByServiceLevel(
      quotes,
      shipmentMethodValue as ShipmentMethodBucket,
      serviceLevelFilter
    );

    if (visibleQuotes.length === 0) {
      return (
        <div className='flex flex-col gap-6'>
          {methodSwitcher}
          {staleQuoteNotice}
          {noQuotesEmptyState(
            serviceLevelFilter
              ? `No ${serviceLevelFilter} quotes on this route. Switch the service level to Any to see every quote, or continue and our carrier-ops team will send a manual quote.`
              : noMatchingQuotesCopy
          )}
        </div>
      );
    }

    const scrollLeft = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
      }
    };

    const scrollRight = () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
      }
    };

    return (
      <div className='flex flex-col gap-6'>
        {methodSwitcher}
        {staleQuoteNotice}

        <div className='flex items-center justify-between'>
          <div>
            <h2 className='text-lg font-semibold text-gray-900'>
              Best options for your route
            </h2>
            <p className='text-sm text-gray-500'>
              Choose the best carrier with an optimal route schedule and price.
            </p>
          </div>
          <div className='flex items-end gap-2'>
            {showServiceLevelFilter && serviceLevelSelect}
            <button
              type='button'
              onClick={scrollLeft}
              className='p-2 rounded-full border border-gray-200 hover:bg-gray-50'
            >
              <svg
                className='w-4 h-4 text-gray-500'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M15 19l-7-7 7-7'
                />
              </svg>
            </button>
            <button
              type='button'
              onClick={scrollRight}
              className='p-2 rounded-full border border-gray-200 hover:bg-gray-50'
            >
              <svg
                className='w-4 h-4 text-gray-500'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 5l7 7-7 7'
                />
              </svg>
            </button>
          </div>
        </div>

        <div ref={scrollContainerRef} className='w-full overflow-x-auto pt-3'>
          <div
            className='grid w-max gap-3 mb-4 mx-auto'
            style={{
              gridTemplateColumns: `repeat(${visibleQuotes.length}, 220px)`,
            }}
          >
            {visibleQuotes.map((quote, index) => {
              const isSelected =
                !!selectedQuote &&
                isSameQuote(
                  quote,
                  selectedQuote,
                  selectedQuoteServiceName,
                  selectedQuoteVehicleType
                );
              return (
                <QuoteOptionCard
                  key={`${getQuoteKey(quote)}|${index}`}
                  quote={quote}
                  isSelected={isSelected}
                  onSelect={() => handleQuoteSelection(quote)}
                />
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderStep3 = () => {
    // Confirmation step is a read-only summary. Every value is derived from form
    // state (or the selected quote) — no placeholder data.
    const formValues = getValues();
    const selectedQuoteEntry = formValues.selectedRateCardId
      ? quotes.find((q) =>
          isSameQuote(
            q,
            formValues.selectedRateCardId,
            formValues.selectedQuoteServiceName,
            formValues.selectedQuoteVehicleType
          )
        )
      : undefined;
    const isSelectedQuoteUnconfirmed =
      !!formValues.selectedRateCardId && !selectedQuoteEntry;

    const formatQuantity = (qty: string | number) => {
      const num = Number(qty);
      if (!Number.isFinite(num) || num <= 0) return '—';
      return `${num} ${num === 1 ? 'pallet' : 'pallets'}`;
    };

    const formatWeight = (w: string | number, unit: string) => {
      const num = Number(w);
      if (!Number.isFinite(num) || num <= 0) return '—';
      return `${num.toLocaleString()} ${unit || 'kg'}`;
    };

    const formatDimension = (v: string | number, unit: string) => {
      const num = Number(v);
      if (!Number.isFinite(num) || num <= 0) return '—';
      return `${num} ${unit || ''}`.trim();
    };

    const formatTimeWindow = (from?: string, to?: string) => {
      if (!from && !to) return '—';
      if (from && to) return `${from} - ${to}`;
      return from || to || '—';
    };

    const pickupLine =
      formValues.pickupFullAddress ||
      formValues.pickupCompanyName ||
      formValues.collectionPostcode ||
      '—';
    const dropoffLine =
      formValues.dropoffFullAddress ||
      formValues.dropoffCompanyName ||
      formValues.deliveryPostcode ||
      '—';
    const specialInstructions =
      formValues.briefContents || formValues.pickupAdditionalNotes || '—';

    return (
      <div className='flex flex-col gap-y-6'>
        {/* Shipment details */}
        <div className='bg-white rounded-lg border border-gray-200 p-5'>
          <div className='flex justify-between items-center mb-4'>
            <h3 className='text-base font-semibold text-gray-900'>
              Shipment details
            </h3>
            <button
              type='button'
              onClick={() => setCurrentStep(1)}
              className='text-sm text-primary font-medium hover:underline'
            >
              Edit
            </button>
          </div>

          <div className='flex flex-col gap-y-2'>
            {(purchaseOrderNumber || formValues.bookingReference) && (
              <div className='flex'>
                <span className='text-sm text-gray-500 w-40'>
                  Purchase order #:
                </span>
                <span className='text-sm text-gray-900'>
                  {purchaseOrderNumber || formValues.bookingReference}
                </span>
              </div>
            )}
            {despatchNoteNumber && (
              <div className='flex'>
                <span className='text-sm text-gray-500 w-40'>
                  Despatch note:
                </span>
                <span className='text-sm text-gray-900'>
                  {despatchNoteNumber}
                </span>
              </div>
            )}
            <div className='flex flex-col gap-y-1 mt-2'>
              <div className='flex'>
                <span className='text-sm text-gray-500 w-40'>Collection:</span>
                <span className='text-sm text-gray-900'>
                  {formValues.pickupCompanyName || '—'}
                </span>
              </div>
              {pickupLine && pickupLine !== formValues.pickupCompanyName && (
                <div className='flex'>
                  <span className='text-sm text-gray-500 w-40' />
                  <span className='text-sm text-gray-500'>{pickupLine}</span>
                </div>
              )}
            </div>
            <div className='flex flex-col gap-y-1 mt-2'>
              <div className='flex'>
                <span className='text-sm text-gray-500 w-40'>Delivery:</span>
                <span className='text-sm text-gray-900'>
                  {formValues.dropoffCompanyName || '—'}
                </span>
              </div>
              {dropoffLine && dropoffLine !== formValues.dropoffCompanyName && (
                <div className='flex'>
                  <span className='text-sm text-gray-500 w-40' />
                  <span className='text-sm text-gray-500'>{dropoffLine}</span>
                </div>
              )}
            </div>
            <div className='flex mt-2'>
              <span className='text-sm text-gray-500 w-40'>
                Requested time of arrival:
              </span>
              <span className='text-sm text-gray-900'>
                {formatLocaleDate(formValues.deliveryDate)}
              </span>
            </div>
            <div className='flex mt-2'>
              <span className='text-sm text-gray-500 w-40'>
                Special instructions:
              </span>
              <span className='text-sm text-gray-900'>
                {specialInstructions}
              </span>
            </div>
          </div>
        </div>

        {/* Load overview */}
        <div className='bg-white rounded-lg border border-gray-200 p-5'>
          <div className='flex justify-between items-center mb-4'>
            <h3 className='text-base font-semibold text-gray-900'>
              Load overview
            </h3>
            <button
              type='button'
              onClick={() => setCurrentStep(1)}
              className='text-sm text-primary font-medium hover:underline'
            >
              Edit
            </button>
          </div>

          <div className='flex justify-between items-center py-2 border-b border-gray-100'>
            <span className='text-sm text-gray-500'>Total pallets</span>
            <span className='text-sm text-gray-900'>
              {formatQuantity(formValues.totalPallets)}
            </span>
          </div>

          <div className='grid grid-cols-[1fr_80px_80px_80px] gap-4 py-2 border-b border-gray-50 mt-3'>
            <div>
              <p className='text-sm text-gray-900'>
                {formValues.palletType || 'Pallet'}
              </p>
              {formValues.palletLength && formValues.palletWidth && (
                <p className='text-xs text-gray-500'>
                  {formValues.palletLength}×{formValues.palletWidth}{' '}
                  {formValues.heightUnit || ''}
                </p>
              )}
            </div>
            <div>
              <p className='text-xs text-gray-500'>Quantity</p>
              <p className='text-sm text-gray-900'>
                {formValues.totalPallets || '—'}
              </p>
            </div>
            <div>
              <p className='text-xs text-gray-500'>Height</p>
              <p className='text-sm text-gray-900'>
                {formatDimension(formValues.height, formValues.heightUnit)}
              </p>
            </div>
            <div>
              <p className='text-xs text-gray-500'>Stackable</p>
              <p className='text-sm text-gray-900'>
                {formValues.stackable ? 'Yes' : 'No'}
              </p>
            </div>
          </div>

          <div className='flex justify-between items-center pt-3 mt-2 border-t border-gray-200'>
            <span className='text-sm text-gray-500'>Total weight</span>
            <span className='text-sm font-semibold text-gray-900'>
              {formatWeight(formValues.weight, formValues.weightUnit)}
            </span>
          </div>
        </div>

        {/* Route */}
        <div className='bg-white rounded-lg border border-gray-200 p-5'>
          <div className='flex justify-between items-center mb-4'>
            <h3 className='text-base font-semibold text-gray-900'>Route</h3>
            <button
              type='button'
              onClick={() => setCurrentStep(1)}
              className='text-sm text-primary font-medium hover:underline'
            >
              Edit
            </button>
          </div>

          <div className='flex items-center gap-2 mb-4'>
            <span className='text-sm text-gray-500'>Collection date:</span>
            <span className='text-sm text-gray-900'>
              {formatLocaleDate(formValues.collectionDate)}
            </span>
          </div>

          <div className='grid grid-cols-[1fr_120px_120px] gap-4 py-2 border-b border-gray-200'>
            <span className='text-sm text-gray-500'>Point</span>
            <span className='text-sm text-gray-500'>Postcode</span>
            <span className='text-sm text-gray-500'>Window</span>
          </div>

          <div className='grid grid-cols-[1fr_120px_120px] gap-4 py-3 border-b border-gray-50'>
            <div className='flex items-center gap-2'>
              <span className='text-primary'>📍</span>
              <span className='text-sm text-gray-900'>
                {formValues.pickupCompanyName || pickupLine}
              </span>
            </div>
            <span className='text-sm text-gray-900'>
              {formValues.collectionPostcode || '—'}
            </span>
            <span className='text-sm text-gray-900'>
              {formatTimeWindow(
                formValues.collectionTimeFrom,
                formValues.collectionTimeTo
              )}
            </span>
          </div>
          <div className='grid grid-cols-[1fr_120px_120px] gap-4 py-3 border-b border-gray-50'>
            <div className='flex items-center gap-2'>
              <span className='text-primary'>📍</span>
              <span className='text-sm text-gray-900'>
                {formValues.dropoffCompanyName || dropoffLine}
              </span>
            </div>
            <span className='text-sm text-gray-900'>
              {formValues.deliveryPostcode || '—'}
            </span>
            <span className='text-sm text-gray-900'>
              {formatTimeWindow(
                formValues.deliveryTimeFrom,
                formValues.deliveryTimeTo
              )}
            </span>
          </div>
        </div>

        {/* Carrier / selected quote */}
        <div className='bg-white rounded-lg border border-gray-200 p-5'>
          <div className='flex justify-between items-center mb-4'>
            <h3 className='text-base font-semibold text-gray-900'>
              Selected quote
            </h3>
            <button
              type='button'
              onClick={() => setCurrentStep(2)}
              className='text-sm text-primary font-medium hover:underline'
            >
              Edit
            </button>
          </div>

          {selectedQuoteEntry ? (
            <div className='flex flex-col gap-y-2'>
              <div className='flex'>
                <span className='text-sm text-gray-500 w-40'>Source:</span>
                <span className='text-sm text-gray-900'>
                  {formatSourceLabel(selectedQuoteEntry.source)}
                </span>
              </div>
              {selectedQuoteEntry.serviceName && (
                <div className='flex'>
                  <span className='text-sm text-gray-500 w-40'>
                    Service level:
                  </span>
                  <span className='text-sm text-gray-900'>
                    {selectedQuoteEntry.serviceName}
                  </span>
                </div>
              )}
              {selectedQuoteEntry.vehicleType && (
                <div className='flex'>
                  <span className='text-sm text-gray-500 w-40'>
                    Vehicle type:
                  </span>
                  <span className='text-sm text-gray-900'>
                    {formatVehicleLabel(selectedQuoteEntry.vehicleType)}
                  </span>
                </div>
              )}
              {selectedQuoteEntry.source === 'mileage' &&
                typeof selectedQuoteEntry.distanceMiles === 'number' && (
                  <div className='flex'>
                    <span className='text-sm text-gray-500 w-40'>
                      Distance:
                    </span>
                    <span className='text-sm text-gray-900'>
                      {formatCourierDistance(
                        selectedQuoteEntry.distanceMiles,
                        selectedQuoteEntry.chargedMiles
                      )}
                    </span>
                  </div>
                )}
              <div className='flex'>
                <span className='text-sm text-gray-500 w-40'>Price:</span>
                <span className='text-sm font-semibold text-gray-900'>
                  {formatPrice(
                    selectedQuoteEntry.price,
                    selectedQuoteEntry.currency
                  )}
                </span>
              </div>
            </div>
          ) : isSelectedQuoteUnconfirmed ? (
            <div className='flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200'>
              <span className='text-amber-500 mt-0.5'>⚠</span>
              <div className='flex-1'>
                <p className='text-sm text-amber-800'>
                  {quotesError ?? 'Searching quotes and building routes...'}
                </p>
                {quotesError && (
                  <button
                    type='button'
                    onClick={() => fetchQuotes()}
                    className='mt-2 text-sm font-medium text-primary hover:underline'
                  >
                    Retry
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className='flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200'>
              <span className='text-amber-500 mt-0.5'>⚠</span>
              <div className='flex-1'>
                {isQuoteSelectionRequired ? (
                  <p className='text-sm text-amber-800'>
                    No quote selected yet. Go back to step 2 and select a quote
                    to place this order.
                  </p>
                ) : (
                  <p className='text-sm text-amber-800'>
                    No quote selected yet. You can submit the order now and our
                    carrier-ops team will send a manual quote, or go back to
                    pick one.
                  </p>
                )}
                <button
                  type='button'
                  onClick={() => setCurrentStep(2)}
                  className='mt-2 text-sm font-medium text-primary hover:underline'
                >
                  Go back to step 2
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      default:
        return renderStep1();
    }
  };

  return (
    <FormPage<TransportOrderFormValues>
      header={header}
      pages={pages}
      methods={methods}
      steps={STEPS}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
      stepFields={stepFields}
    >
      {renderCurrentStep()}
    </FormPage>
  );
}
